import { useState } from 'react';
import { createHero, getHero, updateHero } from '../../../api/apiServices';

export default function HeroSection() {
  const [hero, setHero] = useState({
  title: "",
  subtitle: "",
  description: "",
  primaryButtonText: "",
  secondaryButtonText: "",
  backgroundImage: "",   // ← this one is still used for preview
  isActive: true,
});

  const [backgroundFile, setBackgroundFile] = useState(null);
  const [exists, setExists] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  /* ---------------------------------- LOAD ---------------------------------- */
  useEffect(() => {
  const fetchHero = async () => {
    try {
      const res = await getHero();
      if (res?.data) {
        setHero(res.data);
        setExists(true);
      }
    } catch {
      setExists(false);
    }
  };
  fetchHero();
}, []);

  /* ---------------------------------- SAVE ---------------------------------- */
  const handleSave = async () => {
  try {
    setIsSaving(true);

    const formData = new FormData();

    formData.append("title", hero.title);
    formData.append("subtitle", hero.subtitle);
    formData.append("description", hero.description);
    formData.append("primaryButtonText", hero.primaryButtonText);
    formData.append("secondaryButtonText", hero.secondaryButtonText);
    formData.append("isActive", hero.isActive);

    if (backgroundFile) {
      formData.append("backgroundImage", backgroundFile);
    }

    if (exists) {
      await updateHero(formData);
    } else {
      await createHero(formData);
      setExists(true);
    }

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  } catch (err) {
    console.error("Hero save failed:", err);
  } finally {
    setIsSaving(false);
  }
};

  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Hero Section</h2>
          <p className="text-sm text-gray-600">
            Customize your homepage hero area
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <i className="ri-loader-4-line animate-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="ri-save-line" /> Save Changes
            </>
          )}
        </button>
      </div>

      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-check-line" />
          Hero section saved successfully
        </div>
      )}

      {/* ================= SITE STATUS ================= */}
    <section className="bg-white rounded-xl border shadow-sm">
      <div className="border-b px-6 py-4">
        <h3 className="font-semibold text-gray-900">Hero Status</h3>
      </div>

      <div className="p-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-800">
            Hero Active
          </p>
          <p className="text-xs text-gray-500">
            Disable hero content temporarily 
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            setHero({ ...hero, isActive: !hero.isActive })
          }
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
            hero.isActive ? "bg-[#6a4bc3]" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              hero.isActive ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </section>

      {/* PREVIEW */}
      {hero.backgroundImage && (
        <div className="relative h-64 rounded-xl overflow-hidden">
          <img
            src={hero.backgroundImage}
            className="w-full h-full object-cover"
            alt="Hero preview"
          />
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-center p-6">
            <div className="text-white">
              <p className="text-[#c9a24d] text-xs mb-2">{hero.subtitle}</p>
              <h1 className="text-2xl font-bold mb-2">
                {hero.title}
              </h1>
              <p className="text-sm text-white/80">{hero.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* BACKGROUND IMAGE */}
      <div className="bg-white p-6 rounded-xl border">
        <h3 className="font-semibold mb-3">Background Image</h3>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            setBackgroundFile(e.target.files[0]);
            setHero({
              ...hero,
              backgroundImage: URL.createObjectURL(e.target.files[0]),
            });
          }}
        />
      </div>

      {/* TEXT */}
      <div className="bg-white p-6 rounded-xl border space-y-4">
        <input
          className="input"
          placeholder="Subtitle"
          value={hero.subtitle}
          onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
        />

          <input
            className="input"
            placeholder="Title"
            value={hero.title}
            onChange={(e) => setHero({ ...hero, title: e.target.value })}
          />

        <textarea
          rows={3}
          className="input"
          placeholder="Description"
          value={hero.description}
          onChange={(e) =>
            setHero({ ...hero, description: e.target.value })
          }
        />
      </div>

      {/* BUTTONS */}
      <div className="bg-white p-6 rounded-xl border grid md:grid-cols-2 gap-4">
        <input
          className="input"
          placeholder="Primary Button Text"
          value={hero.primaryButtonText}
          onChange={(e) =>
            setHero({ ...hero, primaryButtonText: e.target.value })
          }
        />
        <input
          className="input"
          placeholder="Secondary Button Text"
          value={hero.secondaryButtonText}
          onChange={(e) =>
            setHero({ ...hero, secondaryButtonText: e.target.value })
          }
        />
      </div>

      {/* CARD IMAGES */}
      {/* <div className="bg-white p-6 rounded-xl border space-y-4">
  <div className="flex items-center justify-between">
    <h3 className="font-semibold">Feature Card Images</h3>
    <button
      type="button"
      onClick={addCardImage}
      className="text-sm text-[#6a4bc3] font-medium"
    >
      + Add Card
    </button>
  </div>

  {cardPreviews.map((imgSrc, i) => (
  <div key={i} className="flex items-center gap-4">
    {imgSrc && (
      <img
        src={imgSrc}
        className="w-20 h-24 object-cover rounded-lg border"
        alt=""
      />
    )}

    <input
      type="file"
      accept="image/*"
      onChange={(e) => {
        if (e.target.files?.[0]) {
          updateCardImage(i, e.target.files[0]);
        }
      }}
    />

    <button
      type="button"
      onClick={() => removeCardImage(i)}
      className="text-red-500 text-sm"
    >
      Remove
    </button>
  </div>
))}

  {cardImages.length === 0 && (
    <p className="text-sm text-gray-500">
      No card images added yet
    </p>
  )}
</div> */}

    </div>
  );
}
